// =============================================================================
// SIREPPAR - Hooks de alertas del dashboard
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export type TipoAlerta =
  | 'OBSERVACION_PENDIENTE'
  | 'MANDATO_POR_VENCER'
  | 'RENDICION_ANUAL_POR_VENCER'
  | 'RENDICION_CAMPANA_POR_VENCER'
  | 'RESPUESTA_PENDIENTE_REVISION'
  | 'INTERACCION_PENDIENTE'
  // Consultas a la SEP (#391): la SEP ve las pendientes de respuesta y el
  // partido, las que la Secretaría ya respondió.
  | 'CONSULTA_PENDIENTE'
  | 'CONSULTA_RESPONDIDA'
  // Consulta que le inició la SEP al partido y que todavía no contestó.
  | 'CONSULTA_RECIBIDA'
  // Constitución de alianza. Le llegan tanto a la alianza como a cada partido
  // integrante registrado en SIREPPAR: el expediente no es "de" un integrante,
  // pero el estado de la alianza los afecta a todos.
  | 'ALIANZA_OBSERVACION_PENDIENTE'
  | 'ALIANZA_EN_ANALISIS'
  | 'ALIANZA_EN_SECRETARIO'
  | 'ALIANZA_RECONOCIDA'
  | 'ALIANZA_RECHAZADA';

export type SeveridadAlerta = 'CRITICA' | 'ALTA' | 'INFO';

export interface Alerta {
  id: string;
  tipo: TipoAlerta;
  severidad: SeveridadAlerta;
  titulo: string;
  detalle: string;
  fechaLimite: string | null;
  diasRestantes: number | null;
  linkHref: string;
}

export interface AlertasResponse {
  total: number;
  alertas: Alerta[];
}

/**
 * Texto contextual del plazo de una alerta. En lugar de un genérico "10 días"
 * o "Vencido", aclara QUÉ hay que hacer dentro de ese plazo (responder,
 * revisar, presentar) o QUÉ fue lo que venció (el mandato, el expediente, el
 * plazo de respuesta o de revisión). `diasRestantes === 0` significa vencido.
 */
export function formatPlazoAlerta(alerta: Alerta): string {
  const dias = alerta.diasRestantes;
  if (dias === null) return '';
  const vencido = dias === 0;
  const cuenta = `${dias} ${dias === 1 ? 'día' : 'días'}`;
  switch (alerta.tipo) {
    case 'OBSERVACION_PENDIENTE':
    case 'ALIANZA_OBSERVACION_PENDIENTE':
      return vencido ? 'Plazo para responder vencido' : `${cuenta} para responder`;
    case 'RESPUESTA_PENDIENTE_REVISION':
      return vencido ? 'Plazo para revisar vencido' : `${cuenta} para revisar`;
    case 'MANDATO_POR_VENCER':
      return vencido ? 'Mandato vencido' : `Vence en ${cuenta}`;
    case 'RENDICION_ANUAL_POR_VENCER':
    case 'RENDICION_CAMPANA_POR_VENCER':
      return vencido ? 'Presentación vencida' : `${cuenta} para presentar`;
    default:
      return vencido ? 'Vencido' : cuenta;
  }
}

const ROLES_PARTIDO = ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE'];
const ROLES_INTERNOS = [
  'MESA_ENTRADAS',
  'ANALISTA',
  'SUPERVISOR',
  'AUTORIDAD_FIRMANTE',
  'ADMINISTRADOR',
  'AUDITOR',
];

export function useAlertasPartido() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['dashboard-alertas'],
    queryFn: async () => {
      const response = await api.get('/dashboard/alertas');
      return response.data.data as AlertasResponse;
    },
    enabled: ROLES_PARTIDO.includes(user?.rol || ''),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useAlertasInternas() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['dashboard-alertas-internas'],
    queryFn: async () => {
      const response = await api.get('/dashboard/alertas-internas');
      return response.data.data as AlertasResponse;
    },
    enabled: ROLES_INTERNOS.includes(user?.rol || ''),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Hook combinado: devuelve las alertas del usuario actual, sea de partido o
 * interno. Útil para componentes como la campanita del header que no necesitan
 * distinguir el origen.
 */
export function useAlertasUsuario() {
  const partido = useAlertasPartido();
  const internas = useAlertasInternas();
  const data = partido.data ?? internas.data;
  const isLoading = partido.isLoading || internas.isLoading;
  return { data, isLoading };
}
